import { inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, map } from 'rxjs/operators';
import { of } from 'rxjs';
import * as L from 'leaflet';
import { MapModeType } from '../models/map-mode.model';
import { WSPlayer } from '../models/WS-player.model';
import { PlayerMarkerType } from '../models/player-marker.model';
import { PlayerClusterType } from '../models/player-cluster.model';
import {
  buildPlayerClusters,
  fitPolylineBounds,
  getClusterKey,
  removeDisplayedClusterMarker,
  removeDisplayedClusterMarkersForPlayers,
  removeDisplayedPlayerMarker,
  removeDisplayedPlayerMarkers,
} from '../helpers/leaflet.helpers';
import { HttpClient } from '@angular/common/http';

type DisplayedPlayerMarkers = globalThis.Map<string, L.Marker>;

type SyncPlayersLayersOptions = {
  map: L.Map;
  playersMarkers: PlayerMarkerType[];
  displayedPlayerMarkers: DisplayedPlayerMarkers;
  displayedClusterMarkers: DisplayedPlayerMarkers;
};

type CreateMarkerOptions = {
  markerUrl?: string;
  html?: string;
  className?: string;
  size?: 'large' | 'medium' | 'small';
  anchor?: [number, number];
  zIndexOffset?: number;
};

@Injectable({
  providedIn: 'root',
})
export class LeafletService {
  private http: HttpClient = inject(HttpClient);
  private readonly MAX_CLUSTER_DISTANCE: number = 20;
  private readonly FRANCE_COORDS: L.LatLngLiteral = { lat: 46.2276, lng: 2.2137 };
  public readonly INITIAL_MAP_OPTION: L.MapOptions = {
    center: [this.FRANCE_COORDS.lat, this.FRANCE_COORDS.lng],
    zoom: 7,
    minZoom: 3,
    maxBounds: new L.LatLngBounds(L.latLng(-90, -180), L.latLng(90, 180)),
    doubleClickZoom: false,
    zoomControl: false,
    layers: [],
    attributionControl: false,
  };

  private readonly TILE_LAYERS: Record<
    MapModeType,
    { url: string; options: L.TileLayerOptions; overlayUrl?: string }
  > = {
    plan: {
      url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
      options: {
        noWrap: true,
        crossOrigin: 'anonymous',
        tileSize: 256,
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      },
    },
    satellite: {
      url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      options: {
        noWrap: true,
        crossOrigin: 'anonymous',
        tileSize: 256,
        attribution:
          '&copy; <a href="https://www.esri.com/">Esri</a>, Maxar, Earthstar Geographics',
      },
    },
    hybrid: {
      url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      options: {
        noWrap: true,
        crossOrigin: 'anonymous',
        tileSize: 256,
        attribution:
          '&copy; <a href="https://www.esri.com/">Esri</a>, Maxar, Earthstar Geographics',
      },
      overlayUrl:
        'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
    },
  };

  createTileLayers(type: MapModeType): L.TileLayer[] {
    const config: {
      url: string;
      options: L.TileLayerOptions;
      overlayUrl?: string;
    } = this.TILE_LAYERS[type];
    const layers: L.TileLayer[] = [L.tileLayer(config.url, config.options)];
    if (config.overlayUrl) {
      layers.push(L.tileLayer(config.overlayUrl, { ...config.options }));
    }
    return layers;
  }

  createPolyline(latlngs: L.LatLngExpression[], color: string, width: number = 5): L.Polyline {
    return L.polyline(latlngs, {
      color: color,
      weight: width,
      smoothFactor: 0,
    });
  }

  replaceTileLayers(map: L.Map, currentLayers: L.TileLayer[], type: MapModeType): L.TileLayer[] {
    currentLayers.forEach((layer: L.TileLayer) => map.removeLayer(layer));

    const nextLayers: L.TileLayer[] = this.createTileLayers(type);
    nextLayers.forEach((layer: L.TileLayer) => layer.addTo(map));

    return nextLayers;
  }

  scheduleViewportSyncToPolyline(
    map: L.Map | null,
    coordinates: L.LatLngExpression[],
    refreshFrameId: number | null,
    onSynced: () => void,
  ): number {
    if (refreshFrameId !== null) {
      cancelAnimationFrame(refreshFrameId);
    }

    return requestAnimationFrame(() => {
      onSynced();
      if (!map || coordinates.length === 0) return;

      map.invalidateSize({ pan: false });
      fitPolylineBounds(map, coordinates);
    });
  }

  createPlayerMarker(player: WSPlayer, isClient: boolean): PlayerMarkerType {
    const coordinates: L.LatLng = L.latLng(player.data.coordinates[0], player.data.coordinates[1]);

    return {
      user: player.user,
      coordinates,
      isClient,
      marker: this.createMarker(coordinates, {
        markerUrl: player.user.avatarUrl,
        className: `players-marker ${isClient ? 'client' : ''}`,
        size: isClient ? 'medium' : 'small',
        zIndexOffset: isClient ? 1000 : 0,
      }),
    };
  }

  createClusterMarker(cluster: PlayerClusterType): L.Marker {
    return this.createMarker(cluster.center, {
      html: `
        <img src="icons/social.svg" alt="Cluster Icon" />
        <span>${cluster.players.length}</span>
      `,
      className: 'players-cluster-marker',
      anchor: [20, 20],
      size: 'large',
    });
  }

  syncDisplayedPlayersLayers({
    map,
    playersMarkers,
    displayedPlayerMarkers,
    displayedClusterMarkers,
  }: SyncPlayersLayersOptions): void {
    const nextDisplayedPlayerKeys: Set<string> = new Set<string>();
    const nextDisplayedClusterKeys: Set<string> = new Set<string>();

    buildPlayerClusters(map, playersMarkers, this.MAX_CLUSTER_DISTANCE).forEach(
      (cluster: PlayerClusterType) => {
        if (cluster.players.length === 1) {
          const playerMarker: PlayerMarkerType = cluster.players[0];
          const playerKey: string = playerMarker.user.username;

          nextDisplayedPlayerKeys.add(playerKey);
          removeDisplayedClusterMarkersForPlayers(map, displayedClusterMarkers, [playerKey]);

          if (!displayedPlayerMarkers.has(playerKey)) {
            playerMarker.marker.addTo(map);
            displayedPlayerMarkers.set(playerKey, playerMarker.marker);
          }

          playerMarker.marker.setLatLng(playerMarker.coordinates);
          return;
        }

        const clusterKey: string = getClusterKey(cluster);
        nextDisplayedClusterKeys.add(clusterKey);

        removeDisplayedPlayerMarkers(
          map,
          displayedPlayerMarkers,
          cluster.players.map((player: PlayerMarkerType) => player.user.username),
        );

        const existingClusterMarker: L.Marker | undefined = displayedClusterMarkers.get(clusterKey);
        if (existingClusterMarker) {
          existingClusterMarker.setLatLng(cluster.center);
          return;
        }

        const clusterMarker: L.Marker = this.createClusterMarker(cluster);
        clusterMarker.addTo(map);
        displayedClusterMarkers.set(clusterKey, clusterMarker);
      },
    );

    Array.from(displayedPlayerMarkers.keys()).forEach((playerKey: string) => {
      if (!nextDisplayedPlayerKeys.has(playerKey)) {
        removeDisplayedPlayerMarker(map, displayedPlayerMarkers, playerKey);
      }
    });

    Array.from(displayedClusterMarkers.keys()).forEach((clusterKey: string) => {
      if (!nextDisplayedClusterKeys.has(clusterKey)) {
        removeDisplayedClusterMarker(map, displayedClusterMarkers, clusterKey);
      }
    });
  }

  createMarker(location: L.LatLngLiteral, options: CreateMarkerOptions): L.Marker {
    const {
      markerUrl,
      html = markerUrl ? `<img src='${markerUrl}'/>` : '',
      className = 'icon-marker',
      size = 'medium',
      anchor,
      zIndexOffset = 0,
    }: CreateMarkerOptions = options;

    let iconSize: L.PointExpression = [32, 32];

    switch (size) {
      case 'small':
        iconSize = [24, 24];
        break;
      case 'large':
        iconSize = [40, 40];
        break;
    }

    const markerOption: L.MarkerOptions = {
      zIndexOffset,
      icon: new L.DivIcon({
        html,
        iconSize,
        iconAnchor: anchor,
        className,
      }),
    };
    const marker: L.Marker = new L.Marker(location, markerOption);
    return marker;
  }

  removePlayerFromDisplayedLayers(
    map: L.Map | null,
    displayedPlayerMarkers: DisplayedPlayerMarkers,
    displayedClusterMarkers: DisplayedPlayerMarkers,
    playerKey: string,
  ): void {
    if (!map) return;
  }

  get tilesIsDisplayable(): Signal<boolean> {
    const testTileUrl: string = 'https://tile.openstreetmap.org/7/65/45.png';
    return toSignal(
      this.http.get(testTileUrl, { responseType: 'blob' }).pipe(
        map(() => true),
        catchError(() => of(false)),
      ),
      { initialValue: false },
    );
  }
}
